import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/payments/status/${sid}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="payment-success-page">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-[#141414] rounded-2xl p-8 border border-white/5">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
                <h1 className="font-heading text-2xl font-bold uppercase text-white mb-3">
                  Processing Payment
                </h1>
                <p className="text-gray-400">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="font-heading text-2xl font-bold uppercase text-white mb-3">
                  Payment Successful!
                </h1>
                <p className="text-gray-400 mb-4">
                  Thank you for your payment of £{paymentData?.amount?.toFixed(2) || '0.00'}
                </p>
                <Link to="/">
                  <Button className="bg-primary text-white hover:bg-primary/90">
                    Back to Home
                  </Button>
                </Link>
              </>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="font-heading text-2xl font-bold uppercase text-white mb-3">
                  {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
                </h1>
                <p className="text-gray-400 mb-4">
                  {status === 'expired' 
                    ? 'Your payment session has expired. Please try again.'
                    : 'There was an issue processing your payment. Please try again or contact support.'}
                </p>
                <Link to="/">
                  <Button className="bg-primary text-white hover:bg-primary/90">
                    Back to Home
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
